// contract: design §3, re-verify at W.1
import { match, P } from 'ts-pattern';
import { z } from 'zod';

import type {
  CryptoAccountStatusOrUnknown,
  CryptoCatalogNetwork,
  CryptoDepositArrival,
  CryptoDepositArrivalStatus,
  CryptoDepositAsset,
  CryptoDepositNetwork,
  ICryptoDepositAddresses,
  ICryptoDepositArrivals,
} from './interfaces/crypto-direct.interface';
import { WHOP_CRYPTO_PROVISIONING_FAILED } from './interfaces/crypto-direct.interface';

export type ContractWarn = (message: string, context?: Record<string, unknown>) => void;

// Dev-only console warning. Context must never contain a deposit address or tx hash.
const devWarn: ContractWarn = (message, context) => {
  if (process.env.NODE_ENV === 'production') return;
  console.warn(`[crypto-direct] ${message}`, context ?? {});
};

export class CryptoDirectContractError extends Error {
  readonly code = 'CRYPTO_DIRECT_CONTRACT';

  constructor(
    public readonly endpoint: 'addresses' | 'deposits',
    public readonly issues: string[]
  ) {
    super(`Unexpected ${endpoint} response shape`);
    this.name = 'CryptoDirectContractError';
  }
}

const issuePaths = (error: z.ZodError) => error.issues.map((issue) => issue.path.join('.'));

const nullableString = z
  .string()
  .nullish()
  .transform((value) => value ?? null);

// EDGE-32497 (design §5 rev 3, panel E5): display-only optional strings from the wire.
// `.catch(null)` so a malformed or NON-STRING value degrades to null INSTEAD of failing
// the network's safeParse — the flatMap in parseCryptoDepositAddresses drops a network
// whose schema fails, and a bad display field must never cost the user the address
// itself. Used by previousAddress. nullableIsoString below is its SIBLING for
// addressChangedAt — the same nullish/catch/transform tolerance with a datetime
// check of its own, declared independently (not derived from this one).
const nullableTolerantString = z
  .string()
  .nullish()
  .catch(null)
  .transform((value) => value ?? null);

// An ISO-8601 instant or null. The wallet emits `Date#toISOString()` (the "Z" form);
// `{ offset: true }` also admits a "+hh:mm" offset so a proxy or a future producer that
// re-serialises with an offset does not silently null the notice.
const nullableIsoString = z
  .string()
  .datetime({ offset: true })
  .nullish()
  .catch(null)
  .transform((value) => value ?? null);

const assetSchema = z.object({
  symbol: z.string().min(1),
  iconUrl: nullableString,
});

// Wire field is `supportedAssets` (proto field 7); the transform below renames it to `assets` on
// the parsed CryptoDepositNetwork. Kept as z.array(z.unknown()) here — per-item parsing/dropping
// happens in parseAssets, mirroring the network/catalog item-level tolerance.
const networkSchema = z.object({
  name: z.string().min(1),
  // EDGE-32101: the v3 READ-ONLY addresses GET may omit `address` (that endpoint never discloses
  // one — the create POST is the sole reveal source). Tolerant `.nullish()` + transform so an
  // address-less GET network is KEPT (address undefined) across EVERY "no address" wire shape —
  // omitted, `null`, or empty string (a proto-JSON serializer possibility) — rather than silently
  // dropped by the flatMap (whose only drop signal is a prod-compiled-out warn). Normalizes to
  // `string | undefined`; the reveal reads address only from the POST, which always carries one.
  address: z
    .string()
    .nullish()
    .transform((value) => value || undefined),
  iconUrl: nullableString,
  // EDGE-32497 (design §5 rev 3): additive optional wire fields (proto 8/9). Both tolerant —
  // a contract violation on a display-only field never drops the network.
  previousAddress: nullableTolerantString,
  addressChangedAt: nullableIsoString,
  supportedAssets: z
    .array(z.unknown())
    .nullish()
    .transform((value) => value ?? []),
});

const catalogSchema = z.object({
  name: z.string().min(1),
  iconUrl: nullableString,
  assets: z
    .array(z.unknown())
    .nullish()
    .transform((value) => value ?? []),
});

const addressesEnvelopeSchema = z.object({
  id: z.string().optional(),
  data: z.object({
    accountStatus: z.string(),
    networks: z
      .array(z.unknown())
      .nullish()
      .transform((value) => value ?? []),
    catalog: z
      .array(z.unknown())
      .nullish()
      .transform((value) => value ?? []),
    disclosedAt: z.string().nullable().optional(),
    // Degrade an unparseable/non-string retryAfterAt to null rather than failing the whole
    // envelope — the rest of this schema's item-level tolerance applies to this field too.
    retryAfterAt: z.string().datetime().nullable().optional().catch(null),
  }),
});

const normalizeAccountStatus = (value: string, warn: ContractWarn): CryptoAccountStatusOrUnknown =>
  match(value)
    .with(
      P.union('active', 'suspended', 'none', 'provisioning', WHOP_CRYPTO_PROVISIONING_FAILED),
      (status) => status
    )
    // EDGE-31825 fail-closed inversion: an unrecognized accountStatus must NEVER be treated as
    // the transient/pollable 'provisioning' — that risks a false "preparing" spinner for a status
    // this FE doesn't know yet. Return the FE-internal 'unknown' sentinel instead; the container
    // fails closed to a generic, non-polling error card.
    .otherwise((unknownStatus) => {
      warn('Unknown accountStatus, treating as unknown (fail-closed)', {
        accountStatus: unknownStatus,
      });
      return 'unknown' as const;
    });

// Per-item asset parsing shared by networks and the catalog. Never include the parent network's
// address in the warn context — only positional/shape info.
const parseAssets = (
  rawAssets: unknown[],
  warn: ContractWarn,
  context: Record<string, unknown>
): CryptoDepositAsset[] =>
  rawAssets.flatMap((item, index): CryptoDepositAsset[] => {
    const result = assetSchema.safeParse(item);
    if (result.success === false) {
      warn('Dropping malformed asset entry', {
        ...context,
        assetIndex: index,
        issues: issuePaths(result.error),
      });
      return [];
    }
    const { symbol, iconUrl } = result.data;
    return [{ symbol, iconUrl }];
  });

export function parseCryptoDepositAddresses(
  raw: unknown,
  warn: ContractWarn = devWarn
): ICryptoDepositAddresses {
  const envelope = addressesEnvelopeSchema.safeParse(raw);
  if (envelope.success === false) {
    throw new CryptoDirectContractError('addresses', issuePaths(envelope.error));
  }

  const { accountStatus, networks, catalog, disclosedAt, retryAfterAt } = envelope.data.data;

  const parsedNetworks = networks.flatMap((item, index): CryptoDepositNetwork[] => {
    const result = networkSchema.safeParse(item);
    if (result.success === false) {
      warn('Dropping malformed network entry', { index, issues: issuePaths(result.error) });
      return [];
    }
    const { name, address, iconUrl, supportedAssets, previousAddress, addressChangedAt } =
      result.data;
    return [
      {
        name,
        address,
        iconUrl,
        assets: parseAssets(supportedAssets, warn, { networkIndex: index }),
        previousAddress,
        addressChangedAt,
      },
    ];
  });

  const parsedCatalog = catalog.flatMap((item, index): CryptoCatalogNetwork[] => {
    const result = catalogSchema.safeParse(item);
    if (result.success === false) {
      warn('Dropping malformed catalog entry', { index, issues: issuePaths(result.error) });
      return [];
    }
    const { name, iconUrl, assets } = result.data;
    return [{ name, iconUrl, assets: parseAssets(assets, warn, { catalogIndex: index }) }];
  });

  return {
    accountStatus: normalizeAccountStatus(accountStatus, warn),
    networks: parsedNetworks,
    catalog: parsedCatalog,
    disclosedAt: disclosedAt ?? null,
    retryAfterAt: retryAfterAt ?? null,
  };
}

const arrivalSchema = z.object({
  id: z.string().min(1),
  status: z.string(),
  // Dollars with cents (23.75) — finite and non-negative, NOT an integer check.
  observedAmount: z.number().finite().nonnegative(),
  currency: z.string().default('USD'),
  chain: z.string().nullable().optional(),
  txHash: z.string().nullable().optional(),
  postedAt: z.string().datetime(),
  creditedAt: z.string().nullable().optional(),
  transactionId: z.string().nullable().optional(),
});

const arrivalsEnvelopeSchema = z.object({
  id: z.string().optional(),
  data: z.object({
    items: z
      .array(z.unknown())
      .nullish()
      .transform((value) => value ?? []),
    nextSince: z.string().nullable().optional(),
  }),
});

const normalizeArrivalStatus = (
  id: string,
  value: string,
  warn: ContractWarn
): CryptoDepositArrivalStatus =>
  match(value)
    .with(P.union('pending', 'under_review', 'completed', 'returned'), (status) => status)
    .otherwise((unknownStatus) => {
      warn('Unknown deposit status, treating as pending', { id, status: unknownStatus });
      return 'pending' as const;
    });

export function parseCryptoDepositArrivals(
  raw: unknown,
  warn: ContractWarn = devWarn
): ICryptoDepositArrivals {
  const envelope = arrivalsEnvelopeSchema.safeParse(raw);
  if (envelope.success === false) {
    throw new CryptoDirectContractError('deposits', issuePaths(envelope.error));
  }

  const items = envelope.data.data.items.flatMap((item, index): CryptoDepositArrival[] => {
    const result = arrivalSchema.safeParse(item);
    if (result.success === false) {
      warn('Dropping malformed deposit entry', { index, issues: issuePaths(result.error) });
      return [];
    }
    const {
      status,
      chain,
      txHash,
      creditedAt,
      transactionId,
      id,
      observedAmount,
      currency,
      postedAt,
    } = result.data;
    return [
      {
        id,
        observedAmount,
        currency,
        postedAt,
        status: normalizeArrivalStatus(id, status, warn),
        chain: chain ?? null,
        txHash: txHash ?? null,
        creditedAt: creditedAt ?? null,
        transactionId: transactionId ?? null,
      },
    ];
  });

  return { items, nextSince: envelope.data.data.nextSince ?? null };
}
